import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { formatCost, formatNumber } from '@/lib/formatters';

type TimeRange = '24h' | '7d' | '30d';
type GroupBy = 'hour' | 'day';

interface AiAnalyticsResponse {
  timeRange: string;
  groupBy: string;
  data: Array<{
    timestamp: number;
    hour?: string;
    executionCount: number;
    totalCost: number;
    avgCost: number;
    avgLatency: number;
    p95Latency: number;
    totalTokensIn: number;
    totalTokensOut: number;
    modelBreakdown: {
      [modelId: string]: {
        count: number;
        cost: number;
        avgLatency: number;
      };
    };
  }>;
  totals: {
    executionCount: number;
    totalCost: number;
    avgLatency: number;
  };
}

export default function AiAnalyticsPage() {
  const authenticatedFetch = useAuthenticatedFetch();
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [groupBy] = useState<GroupBy>('hour');
  const [analytics, setAnalytics] = useState<AiAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchAnalytics() {
      try {
        setLoading(true);
        setError(null);

        const response = await authenticatedFetch(
          `ai-executions/analytics?timeRange=${timeRange}&groupBy=${groupBy}`,
          {
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch analytics (${response.status})`);
        }

        const payload = (await response.json()) as AiAnalyticsResponse;
        if (controller.signal.aborted) {
          return;
        }

        setAnalytics(payload);
      } catch (fetchError) {
        if (controller.signal.aborted) {
          return;
        }
        console.error(fetchError);
        setError(
          fetchError instanceof Error
            ? fetchError.message
            : 'Unable to fetch analytics.',
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    fetchAnalytics();

    return () => controller.abort();
  }, [authenticatedFetch, timeRange, groupBy]);

  const formatChartTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    if (timeRange === '24h') {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const chartData =
    analytics?.data.map((d) => ({
      time: formatChartTime(d.timestamp),
      cost: Number(d.totalCost.toFixed(4)),
      latency: Math.round(d.avgLatency),
      executions: d.executionCount,
    })) ?? [];

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Dashboard', to: '/' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Analytics</h1>
          <p className="text-muted-foreground">
            LLM usage metrics over the last{' '}
            {timeRange === '24h'
              ? '24 hours'
              : timeRange === '7d'
                ? '7 days'
                : '30 days'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/">Dashboard</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/">← Back to Directory</Link>
          </Button>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="flex gap-2">
        {(['24h', '7d', '30d'] as TimeRange[]).map((range) => (
          <Button
            key={range}
            variant={timeRange === range ? 'default' : 'outline'}
            size="sm"
            onClick={() => setTimeRange(range)}
          >
            {range === '24h'
              ? '24 Hours'
              : range === '7d'
                ? '7 Days'
                : '30 Days'}
          </Button>
        ))}
      </div>

      {loading && (
        <div className="text-center text-muted-foreground">
          Loading analytics…
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      )}

      {analytics && !loading && (
        <>
          {/* Key Metrics Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-950 dark:to-background">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  ⏱️ Avg Latency
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {Math.round(analytics.totals.avgLatency)}ms
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Peak:{' '}
                  {analytics.data.length > 0
                    ? Math.round(
                        Math.max(...analytics.data.map((d) => d.p95Latency)),
                      )
                    : 0}
                  ms
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-white dark:from-green-950 dark:to-background">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  $ Total Cost
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {formatCost(analytics.totals.totalCost)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Last{' '}
                  {timeRange === '24h'
                    ? '24 hours'
                    : timeRange === '7d'
                      ? '7 days'
                      : '30 days'}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-white dark:from-purple-950 dark:to-background">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  ⚡ Total Executions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {formatNumber(analytics.totals.executionCount)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Across all hours
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <Card>
            <CardHeader>
              <CardTitle>Latency & Cost Over Time</CardTitle>
              <CardDescription>
                Tracking performance and spending patterns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="time"
                    className="text-xs"
                    tick={{ fill: 'currentColor' }}
                  />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: 'Latency (ms)',
                      angle: -90,
                      position: 'insideLeft',
                    }}
                    tick={{ fill: 'currentColor' }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{
                      value: 'Cost ($)',
                      angle: 90,
                      position: 'insideRight',
                    }}
                    tick={{ fill: 'currentColor' }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                    }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="latency"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    name="Latency (ms)"
                    dot={{ fill: '#8b5cf6', r: 3 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cost"
                    stroke="#10b981"
                    strokeWidth={2}
                    name="Cost ($)"
                    dot={{ fill: '#10b981', r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Hourly Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle>Hourly Breakdown</CardTitle>
              <CardDescription>
                Detailed metrics for each time period
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-hidden rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Hour</TableHead>
                      <TableHead className="text-right">Executions</TableHead>
                      <TableHead className="text-right">Latency</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.data.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono">
                          {new Date(row.timestamp * 1000).toLocaleTimeString(
                            'en-US',
                            {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            },
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatNumber(row.executionCount)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {Math.round(row.avgLatency)}ms
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCost(row.totalCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
